import React, {
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Path, Rect } from 'react-konva';

import ImageAnnotationTransformer from './ImageAnnotationTransformer';

const ImageAnnotation = ({
  shapeProps,
  onChange,
  handleMouseEnter,
  handleMouseLeave,
  annotationId,
  highlighted,
  stageWidth,
  stageHeight,
  commentsAnnotateMode,
  annotationStrokeMargin,
  type,
  identified,
}) => {
  const shapeRef = useRef();
  const transformerRef = useRef();

  useEffect(() => {
    if (commentsAnnotateMode && transformerRef.current) {
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [commentsAnnotateMode]);

  const onMouseEnter = (event) => {
    const newEvent = event;
    if (commentsAnnotateMode) {
      newEvent.target.getStage().container().style.cursor = 'move';
    } else {
      newEvent.target.getStage().container().style.cursor = 'default';
    }
    handleMouseEnter(
      event,
      {
        x: event.target.x(),
        y: event.target.y(),
        width: event.target.width(),
        height: event.target.height(),
      },
      annotationId,
      type,
    );
  };

  const onMouseLeave = event => {
    const newEvent = event;
    newEvent.target.getStage().container().style.cursor = commentsAnnotateMode ? 'crosshair' : 'default';
    handleMouseLeave();
  };

  const getX = (x) => {
    if (x < 0) return 0;
    if (x + shapeProps.width > stageWidth) return stageWidth - shapeProps.width;
    return x;
  };

  const getY = (y) => {
    if (y < 0) return 0;
    if (y + shapeProps.height > stageHeight) return stageHeight - shapeProps.height;
    return y;
  };

  const handleOnChange = (event) => {
    const x = event.target.x();
    const y = event.target.y();
    const newX = getX(x);
    const newY = getY(y);

    onChange({
      meta: {
        ...shapeProps,
        x: newX,
        y: newY,
      },
      id: annotationId,
    });
  };

  const handleOnTransform = () => {
    const node = shapeRef.current;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);

    onChange({
      meta: {
        ...shapeProps,
        x: node.x(),
        y: node.y(),
        // set minimal value
        width: Math.max(5, node.width() * scaleX),
        height: Math.max(node.height() * scaleY),
      },
      id: annotationId,
    });
  };

  const cornerLength = Math.min(shapeProps.height / 50, shapeProps.width / 50);
  const corners = [
    {
      key: `${annotationId}-tl`,
      x: shapeProps.x + 20,
      y: shapeProps.y,
      data: `M ${cornerLength} 0 C -20 0 -20 0 -20 ${cornerLength + 20}`,
    },
    {
      key: `${annotationId}-tr`,
      x: shapeProps.x + shapeProps.width,
      y: shapeProps.y + 20,
      data: `M 0 ${cornerLength} C -0 -20 -0 -20 -${cornerLength + 20} -20`,
    },
    {
      key: `${annotationId}-br`,
      x: shapeProps.x + shapeProps.width - 20,
      y: shapeProps.y + shapeProps.height,
      data: `M -${cornerLength} 0 C 20 -0 20 -0 20 -${cornerLength + 20}`,
    },
    {
      key: `${annotationId}-bl`,
      x: shapeProps.x,
      y: shapeProps.y + shapeProps.height - 20,
      data: `M -0 -${cornerLength} C 0 20 0 20 ${cornerLength + 20} 20`,
    },
  ];

  return (
    <>
      {(type === 'image' || type === 'video') && (
        <>
          <Rect
            fill={highlighted ? '#ff4141' : '#FCE897'}
            opacity={0.4}
            dashEnabled={true}
            dash={[2, 2]}
            stroke="#999"
            ref={shapeRef}
            {...shapeProps}
            draggable={commentsAnnotateMode}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onDragEnd={handleOnChange}
            onTransformEnd={handleOnTransform}
          />
          {commentsAnnotateMode && (
            <ImageAnnotationTransformer
              anchorSize={annotationStrokeMargin}
              keepRatio={false}
              anchorStrokeWidth={0}
              ignoreStroke
              anchorFill="rgba(0,0,0,0)"
              rotateEnabled={false}
              ref={transformerRef}
              borderEnabled={false}
            />
          )}
        </>
      )}
      {type === 'face' && (
        <>
          <Rect
            opacity={0}
            ref={shapeRef}
            {...shapeProps}
            draggable={false}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
          {corners.map(cornerProps => (
            <Path
              {...cornerProps}
              stroke={identified ? '#1adfa2' : '#fff'}
              strokeWidth={4}
            />
          ))}
        </>
      )}
    </>
  );
};

ImageAnnotation.propTypes = {
  shapeProps: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  handleMouseEnter: PropTypes.func.isRequired,
  handleMouseLeave: PropTypes.func.isRequired,
  annotationId: PropTypes.any.isRequired,
  highlighted: PropTypes.bool.isRequired,
  stageWidth: PropTypes.number.isRequired,
  stageHeight: PropTypes.number.isRequired,
  annotationStrokeMargin: PropTypes.number.isRequired,
  commentsAnnotateMode: PropTypes.bool.isRequired,
  type: PropTypes.string,
  identified: PropTypes.bool,
};

ImageAnnotation.defaultProps = {
  type: 'image',
  identified: false,
};

export default ImageAnnotation;
